import type { Emu, SixtyThousandthsDegree } from './units.js';

/** A shape's own `a:xfrm`, in the coordinate space of its immediate parent. */
export interface Transform {
  readonly x: Emu;
  readonly y: Emu;
  readonly cx: Emu;
  readonly cy: Emu;
  readonly rot: SixtyThousandthsDegree;
  readonly flipH: boolean;
  readonly flipV: boolean;
}

/**
 * A group additionally declares the child coordinate space (`a:chOff` / `a:chExt`).
 * Children are authored in that space and must be mapped into the group's own
 * extent. Getting this scaling wrong is the single most common cause of grouped
 * shapes landing in the wrong place.
 */
export interface GroupTransform extends Transform {
  readonly chX: Emu;
  readonly chY: Emu;
  readonly chCx: Emu;
  readonly chCy: Emu;
}

export const IDENTITY_TRANSFORM: Transform = {
  x: 0,
  y: 0,
  cx: 0,
  cy: 0,
  rot: 0,
  flipH: false,
  flipV: false,
};

/** An axis-aligned rectangle in absolute slide coordinates, plus accumulated flips/rotation. */
export interface AbsoluteBounds {
  readonly x: Emu;
  readonly y: Emu;
  readonly cx: Emu;
  readonly cy: Emu;
  readonly rot: SixtyThousandthsDegree;
  readonly flipH: boolean;
  readonly flipV: boolean;
}

/**
 * Maps a child transform from a group's child coordinate space into the group's
 * placed space.
 *
 * ECMA-376 §20.1.7.5: the child space origin (`chOff`) maps to the group origin
 * (`off`), and the child extent (`chExt`) is scaled to the group extent (`ext`).
 * A zero child extent means "no scaling" rather than "collapse to nothing" — the
 * degenerate case appears in decks produced by non-Microsoft tools.
 */
export function applyGroupTransform(child: Transform, group: GroupTransform): AbsoluteBounds {
  const scaleX = group.chCx === 0 ? 1 : group.cx / group.chCx;
  const scaleY = group.chCy === 0 ? 1 : group.cy / group.chCy;

  let localX = (child.x - group.chX) * scaleX;
  let localY = (child.y - group.chY) * scaleY;
  const cx = child.cx * scaleX;
  const cy = child.cy * scaleY;

  // A flip on the group mirrors the placement of its children within the group
  // box, not just the children's own pixels.
  if (group.flipH) localX = group.cx - localX - cx;
  if (group.flipV) localY = group.cy - localY - cy;

  return {
    x: group.x + localX,
    y: group.y + localY,
    cx,
    cy,
    rot: normaliseAngle(child.rot + group.rot),
    flipH: child.flipH !== group.flipH,
    flipV: child.flipV !== group.flipV,
  };
}

export function transformToBounds(t: Transform): AbsoluteBounds {
  return { x: t.x, y: t.y, cx: t.cx, cy: t.cy, rot: t.rot, flipH: t.flipH, flipV: t.flipV };
}

/** Keeps accumulated rotation within a single turn so serialised IR stays comparable. */
export function normaliseAngle(rot: SixtyThousandthsDegree): SixtyThousandthsDegree {
  const full = 360 * 60000;
  return ((rot % full) + full) % full;
}

/** Preset geometry with its adjust values, or a parsed custom path. */
export type ShapeGeometry =
  | { readonly kind: 'preset'; readonly preset: string; readonly adjustments: Readonly<Record<string, number>> }
  | { readonly kind: 'custom'; readonly paths: readonly CustomPath[] }
  | { readonly kind: 'none' };

export interface CustomPath {
  /** Path coordinate space; `0` means "use the shape extent". */
  readonly w: Emu;
  readonly h: Emu;
  readonly fill: 'none' | 'norm' | 'lighten' | 'lightenLess' | 'darken' | 'darkenLess';
  readonly stroke: boolean;
  readonly commands: readonly PathCommand[];
}

export type PathCommand =
  | { readonly op: 'moveTo'; readonly pts: readonly Point[] }
  | { readonly op: 'lnTo'; readonly pts: readonly Point[] }
  | { readonly op: 'cubicBezTo'; readonly pts: readonly Point[] }
  | { readonly op: 'quadBezTo'; readonly pts: readonly Point[] }
  | {
      readonly op: 'arcTo';
      readonly wR: Emu;
      readonly hR: Emu;
      readonly stAng: SixtyThousandthsDegree;
      readonly swAng: SixtyThousandthsDegree;
    }
  | { readonly op: 'close' };

export interface Point {
  readonly x: Emu;
  readonly y: Emu;
}
